import {
  getFirebaseConsoleFirestoreLink,
  getGcpConsoleApiLink,
  getGcpConsoleIamLink,
} from '@/utils/gcp-links';
import { ArrowLeft, Check, Database, LoaderCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useGcpDatabasePicker } from './use-gcp-database-picker';

interface GcpDatabasePickerProps {
  projectId: string;
  onDatabaseSelect: (databaseId: string) => void;
  onBackToProjectSelection?: () => void;
}

export function GcpDatabasePicker({
  projectId,
  onDatabaseSelect,
  onBackToProjectSelection,
}: GcpDatabasePickerProps) {
  const f = useGcpDatabasePicker({ projectId, onDatabaseSelect, onBackToProjectSelection });

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl">
        {f.loading && (
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground flex items-center">
                <LoaderCircle className="h-5 w-5 mr-2 animate-spin" />
                Loading databases...
              </div>
            </CardContent>
          </Card>
        )}

        {f.error && (
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-destructive mb-4">{f.error}</div>
              <div className="flex gap-2">
                {f.isPermissionDenied && f.onBackToProjectSelection && (
                  <Button variant="outline" size="sm" onClick={f.onBackToProjectSelection}>
                    <ArrowLeft className="h-4 w-4" />
                    Select another project
                  </Button>
                )}
                <Button onClick={f.fetchDatabases} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </div>

              {f.isPermissionDenied && (
                <>
                  <hr className="my-4 border-border" />
                  <Alert variant="default" className="bg-transparent border-0 p-0">
                    <AlertTitle className="text-sm font-medium mb-2">
                      Why this is happening
                    </AlertTitle>
                    <AlertDescription className="text-sm text-muted-foreground space-y-3">
                      <p>
                        The selected Google Cloud project doesn't appear to have an accessible
                        Firestore database.
                      </p>
                      <p>This usually means one of the following:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Firestore has not been created yet for this project</li>
                        <li>Firestore exists, but the Firestore Admin API is not enabled</li>
                        <li>
                          Your Google account doesn't have permission to list Firestore databases
                        </li>
                        <li>The project uses Datastore mode, which isn't supported here</li>
                      </ul>
                      <p className="font-medium mt-3">How to fix it:</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li>
                          Open the project in the Firebase Console and make sure Firestore is
                          initialized
                        </li>
                        <li>Enable the Firestore Admin API for the project</li>
                        <li>
                          Ensure your account has a role like Firestore Admin or Datastore Owner
                        </li>
                        <li>If this project doesn't need Firestore, select a different project</li>
                      </ol>
                      <p className="mt-3">
                        Once Firestore is set up and accessible, retry the connection.
                      </p>
                    </AlertDescription>
                  </Alert>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={getFirebaseConsoleFirestoreLink(f.projectId)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Firebase Console
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={getGcpConsoleApiLink(f.projectId)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Enable Firestore Admin API
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={getGcpConsoleIamLink(f.projectId)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Check IAM permissions
                      </a>
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {!f.loading && !f.error && f.databases.length === 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-4">
                No databases found for this project.
              </div>
              {f.onBackToProjectSelection && (
                <Button variant="outline" size="sm" onClick={f.onBackToProjectSelection}>
                  <ArrowLeft className="h-4 w-4" />
                  Select another project
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {!f.loading && !f.error && f.databases.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Database className="h-5 w-5 mr-2" />
                Select Firestore Database
              </h3>
              <div className="space-y-2 mb-4">
                {f.databaseIds.map((databaseId) => (
                  <div
                    key={databaseId}
                    className={`flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${
                      f.selectedDatabaseId === databaseId
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => f.handleItemClick(databaseId)}
                  >
                    <div>
                      <div className="font-medium">{databaseId}</div>
                      <div className="text-sm text-muted-foreground">
                        {f.getDatabaseType(databaseId)}
                      </div>
                    </div>
                    {f.selectedDatabaseId === databaseId && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </div>
                ))}
              </div>
              <Button
                onClick={f.handleSelect}
                disabled={!f.selectedDatabaseId}
                className="w-full mb-2"
              >
                Connect to selected database
              </Button>
              {f.onBackToProjectSelection && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={f.onBackToProjectSelection}
                  className="w-full"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Select another project
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
